import { useEffect, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import { hasPendingNoteWrite, queueNoteBodyUpdate, queueNoteCommit, useManageNote } from './useManageNote';
import { triggerErrorMessage, triggerInfoMessage } from '@/components/ui/toast';
import type { Note } from '@/store/notesStore';
import { useKeydown } from '@/hooks/useKeypress';
import { isCtrlSKey } from '@/utils/keyboard';
import { containsUnencryptedSecretFields } from '@/utils/secretFieldGuard';
import { extractFileIdsFromContent } from '@/utils/fileAttachmentUtils';
import { usePlanInfo } from '@/hooks/usePlanInfo';

export type SavingStatus = {
  state: 'idle' | 'editing' | 'saving' | 'error' | 'viewing';
  /** ISO timestamp of the last persisted note revision (shown in the indicator). */
  contentUpdatedAt?: string;
  /** Name of the note owner — shown in the idle indicator. */
  userName?: string;
  /** ISO timestamp of the version being previewed — shown in the viewing indicator. */
  viewingAt?: string;
};

interface UseSaveNoteProps {
  note: Note | null;
  debounceMs?: number;
  /**
   * Whether the note has a server-side key wrapper.
   * When true, the save guard will block persistence if any secret field
   * still holds plaintext content.
   */
  hasKeyWrapper?: boolean;
  /**
   * When true all saves, Ctrl+S, and commit-on-leave are suppressed.
   * Set while the user is previewing a historical version.
   */
  isReadOnly?: boolean;
  /** Freeze all persistence while a note-wide encryption conversion is active. */
  isEncryptionConversionActive?: boolean;
  /**
   * ISO timestamp of the version being previewed.
   * Passed through to SavingStatus.viewingAt for the indicator.
   */
  viewingAt?: string | null;
}

export function useSaveNote({
  note,
  debounceMs = 1000,
  hasKeyWrapper = false,
  isReadOnly = false,
  isEncryptionConversionActive = false,
  viewingAt = null,
}: UseSaveNoteProps) {
  const { updateOrCreateNote } = useManageNote();
  const writesFrozen = isReadOnly || isEncryptionConversionActive;
  const planInfo = usePlanInfo();
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Becomes true on the first user edit; reset when a new note loads. */
  const isEditingRef = useRef(false);
  /** Last known content row ID — used to detect when a new history row is created. */
  const prevContentIdRef = useRef<number | undefined>(undefined);
  const seqRef = useRef<number>(note?.seq ?? 0);
  const persistedTitleRef = useRef<string>(note?.title ?? '');
  const persistedContentRef = useRef<string>(note?.content ?? '');
  /**
   * Tracks the note ID last seen by the debounce effect.
   * When the note changes (new load / navigation), the debounce effect fires on
   * the same render as the init effect — but setTitle/setContent state updates
   * are not yet applied. Comparing stale title='' with note.title='...' would
   * falsely trigger the editing state. We suppress that check for the single
   * render where the note ID changes.
   */
  const lastNoteIdRef = useRef<string | null | undefined>(undefined);
  const recoveryRestoredRef = useRef<string | null>(null);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState<string | null>(null);
  const [savingStatus, setSavingStatus] = useState<SavingStatus>({ state: 'idle' });
  /** Increments when a save creates a new revision row. */
  const [versionsKey, setVersionsKey] = useState(0);

  // Initialise / reset when the note loads or changes
  useEffect(() => {
    if (note && !note.body_locked) {
      if (note.id && recoveryRestoredRef.current !== note.id) {
        recoveryRestoredRef.current = note.id;
        try {
          const savedDraft = window.sessionStorage.getItem(`note-recovery:${note.id}`);
          if (savedDraft) {
            const recovery = JSON.parse(savedDraft) as { title: string; content: string };
            setTitle(recovery.title);
            setContent(recovery.content);
            triggerInfoMessage('Unsaved text from this tab was restored. Save it after refreshing.');
          } else {
            setTitle(note.title ?? '');
            setContent(note.content ?? '');
          }
        } catch {
          setTitle(note.title ?? '');
          setContent(note.content ?? '');
        }
      }
      const isDraft = note.content_status === 'draft';
      // Treat an existing server-side draft as an active editing session so that
      // navigate-away commit fires even if the user doesn't type anything.
      isEditingRef.current = isDraft;
      prevContentIdRef.current = note.content_id;
      seqRef.current = note.seq ?? 0;
      persistedTitleRef.current = note.title ?? '';
      persistedContentRef.current = note.content ?? '';
      setSavingStatus({
        state: isDraft ? 'editing' : 'idle',
        contentUpdatedAt: note.content_updated_at,
        userName: note.user_name,
      });
      if (!note.id) {
        setTitle(note.title ?? '');
        setContent(note.content ?? '');
      }
    }
  }, [note]);

  const handleSave = async (e?: KeyboardEvent | React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    // Never persist while viewing a historical version
    if (writesFrozen) return false;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    if (!note || content === null || note.body_locked) return false;

    const trimmedTitle = title.trim();
    const trimmedContent = content.trim();

    if (trimmedTitle === persistedTitleRef.current && trimmedContent === persistedContentRef.current) {
      return true;
    }

    // --- Save guard ---
    // If the note has a key wrapper, all secret field values must be encrypted
    // before we allow persistence. Plaintext content must not leave the client.
    if (hasKeyWrapper && containsUnencryptedSecretFields(trimmedContent)) {
      setSavingStatus(prev => ({ ...prev, state: 'error' }));
      triggerErrorMessage(
        'Cannot save — secret fields must be encrypted first. Unlock your master password.',
      );
      setTimeout(() => {
        setSavingStatus(prev => ({
          ...prev,
          state: isEditingRef.current ? 'editing' : 'idle',
        }));
      }, 4000);
      return false;
    }

    try {
      setSavingStatus(prev => ({ ...prev, state: 'saving' }));
      const fileIds = extractFileIdsFromContent(trimmedContent);
      const updatedNote = await updateOrCreateNote(note.id, {
        title: trimmedTitle,
        content: trimmedContent,
        seq: seqRef.current,
        file_ids: fileIds,
        encrypted: Boolean(note.encrypted),
      });
      seqRef.current = updatedNote.seq ?? seqRef.current + 1;
      persistedTitleRef.current = trimmedTitle;
      persistedContentRef.current = trimmedContent;
      if (note.id) {
        try { window.sessionStorage.removeItem(`note-recovery:${note.id}`); } catch { /* storage can be disabled */ }
      }
      // Return to editing (session still active), update the content timestamp
      setSavingStatus(prev => ({
        state: 'editing',
        contentUpdatedAt: updatedNote?.content_updated_at ?? prev.contentUpdatedAt,
        userName: prev.userName,
      }));
      // If a new version row was created (first save of the session), notify the panel
      if (
        updatedNote?.content_id !== undefined &&
        updatedNote.content_id !== prevContentIdRef.current
      ) {
        prevContentIdRef.current = updatedNote.content_id;
        setVersionsKey(k => k + 1);
      }
      return true;
    } catch (error: any) {
      if (note.id && /changed in another tab|changed encryption|refresh this tab/i.test(error?.message ?? '')) {
        try {
          window.sessionStorage.setItem(`note-recovery:${note.id}`, JSON.stringify({ title, content }));
        } catch {
          // Keep the current editor contents available even if session storage is unavailable.
        }
      }
      setSavingStatus(prev => ({ ...prev, state: 'error' }));
      triggerErrorMessage(error?.message || 'Failed to save note. Please try again.');
      setTimeout(() => {
        setSavingStatus(prev => ({
          ...prev,
          state: isEditingRef.current ? 'editing' : 'idle',
        }));
      }, 3000);
      return false;
    }
  };

  useKeydown(isCtrlSKey, handleSave, true);

  // Debounce save + detect first user edit to start the editing session
  useEffect(() => {
    if (!note || note.body_locked) return;

    // Suppress all save scheduling while viewing a historical version
    if (writesFrozen) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      return;
    }

    // When the note ID changes, title/content are still the previous render's
    // stale values — skip edit detection for this one render to avoid a false
    // "editing" state on initial load.
    const noteJustLoaded = lastNoteIdRef.current !== note.id;
    lastNoteIdRef.current = note.id ?? null;

    if (
      !noteJustLoaded &&
      !isEditingRef.current &&
      (title !== (note.title ?? '') || content !== (note.content ?? ''))
    ) {
      isEditingRef.current = true;
      setSavingStatus(prev => ({ ...prev, state: 'editing' }));
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, debounceMs);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [title, content, note, writesFrozen]);

  // Reflect read-only and conversion states in the saving indicator.
  // Uses a ref to detect direction so the effect doesn't fire spuriously on mount.
  const prevIsReadOnlyRef = useRef(false);
  useEffect(() => {
    const wasReadOnly = prevIsReadOnlyRef.current;
    prevIsReadOnlyRef.current = isReadOnly;

    if (writesFrozen) {
      // Entering version preview — show viewing state (viewingAt may arrive async)
      setSavingStatus(prev => ({
        ...prev,
        state: 'viewing',
        viewingAt: viewingAt ?? undefined,
      }));
    } else if (wasReadOnly && note) {
      // Exiting version preview — restore normal editing state
      const isDraft = note.content_status === 'draft';
      isEditingRef.current = isDraft;
      setSavingStatus({
        state: isDraft ? 'editing' : 'idle',
        contentUpdatedAt: note.content_updated_at,
        userName: note.user_name,
      });
    }
  }, [writesFrozen, viewingAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Commit draft on navigate away / tab close
  useEffect(() => {
    const commitOnLeave = (event?: BeforeUnloadEvent | { preventDefault: () => void }) => {
      if (!note || note.body_locked) return;
      // No draft management while viewing a historical version
      if (writesFrozen) return;

      if (!note.id) {
        if (event && 'returnValue' in event) {
          event.preventDefault();
          event.returnValue = '';
        } else {
          event?.preventDefault();
          triggerInfoMessage('This note is still being created. Please navigate away again when it finishes saving.');
        }
        void handleSave();
        return;
      }

      // Cancel any pending debounced save — we handle everything here
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      const trimmedTitle = title.trim();
      const trimmedContent = (content ?? '').trim();
      const hasPendingChanges =
        trimmedTitle !== persistedTitleRef.current || trimmedContent !== persistedContentRef.current;
      const writeAlreadyQueued = hasPendingNoteWrite(note.id);

      if (Boolean(note.encrypted) && hasPendingChanges) {
        if (event && 'returnValue' in event) {
          event.preventDefault();
          event.returnValue = '';
        } else {
          event?.preventDefault();
          void handleSave();
        }
        return;
      }

      // A queued autosave owns the next sequence number. Serialize the leave
      // flush behind it instead of racing it with a second request using the
      // same next_seq value.
      if (writeAlreadyQueued && (hasPendingChanges || isEditingRef.current)) {
        const flush = hasPendingChanges
          ? queueNoteBodyUpdate(note.id, {
              title: trimmedTitle,
              content: trimmedContent,
              commit: true,
              file_ids: extractFileIdsFromContent(trimmedContent),
              encrypted: false,
            }, seqRef.current, true)
          : queueNoteCommit(note.id);

        if (event && 'returnValue' in event) {
          event.preventDefault();
          event.returnValue = '';
        } else {
          event?.preventDefault();
          triggerInfoMessage('A save is in progress. Please navigate away again when it finishes.');
        }

        void flush.then((response) => {
          if (response && typeof response === 'object' && 'note' in response && response.note) {
            const updatedNote = response.note as Note;
            seqRef.current = updatedNote.seq ?? seqRef.current;
            persistedTitleRef.current = trimmedTitle;
            persistedContentRef.current = trimmedContent;
            if (updatedNote.content_id !== undefined) prevContentIdRef.current = updatedNote.content_id;
            setSavingStatus((prev) => ({
              state: 'idle',
              contentUpdatedAt: updatedNote.content_updated_at ?? prev.contentUpdatedAt,
              userName: prev.userName,
            }));
          }
        }).catch((error: any) => {
          triggerErrorMessage(error?.message || 'Failed to save note before leaving.');
        });
        return;
      }

      const fetchOpts: RequestInit = {
        keepalive: true,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      };

      if (hasPendingChanges) {
        // Flush save + commit in one round-trip
        const fileIds = extractFileIdsFromContent(trimmedContent);
        fetch(`/api/app/v1/note/${note.id}`, {
          ...fetchOpts,
          method: 'PUT',
          body: JSON.stringify({
            title: trimmedTitle,
            content: trimmedContent,
            commit: true,
            file_ids: fileIds,
            next_seq: seqRef.current + 1,
          }),
        });
      } else if (isEditingRef.current) {
        // No pending changes but an active draft exists — commit it
        fetch(`/api/app/v1/note/${note.id}/commit`, {
          ...fetchOpts,
          method: 'POST',
        });
      }
    };

    window.addEventListener('beforeunload', commitOnLeave);
    const removeInertiaListener = router.on('before', (event) => commitOnLeave(event));

    return () => {
      window.removeEventListener('beforeunload', commitOnLeave);
      removeInertiaListener();
    };
  }, [title, content, note, writesFrozen]);

  return { title, setTitle, content, setContent, savingStatus, versionsKey, saveCurrent: handleSave, getSeq: () => seqRef.current };
}
